// components/SubirComprobanteTransferencia.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  IconButton,
  Chip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { UrlApi } from '../../../../../services/apirest';

export const SubirComprobanteTransferencia = ({
  open,
  onClose,
  uuidPedido,
  onComprobanteSubido,
  medioPagoNombre = 'Transferencia Bancaria'
}) => {
  const [comprobante, setComprobante] = useState(null);
  const [comprobanteNombre, setComprobanteNombre] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');
  const [exito, setExito] = useState(false);

  // Manejar selección de archivo
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de archivo
      const tiposPermitidos = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!tiposPermitidos.includes(file.type)) {
        setError('Solo se permiten archivos JPG, PNG o PDF');
        return;
      }
      
      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('El archivo no debe superar los 5MB');
        return;
      }
      
      setComprobante(file);
      setComprobanteNombre(file.name);
      setError('');
    }
  };

  // Subir comprobante al servidor (adaptado de tu código React Native)
  const handleSubirComprobante = async () => {
    if (!comprobante) {
      setError('Debe seleccionar un comprobante');
      return;
    }

    if (!uuidPedido) {
      setError('ID de pedido no válido');
      return;
    }

    setSubiendo(true);
    setError('');

    try {
      // Crear FormData igual que en React Native
      const formData = new FormData();
      formData.append("uid", uuidPedido);
      formData.append("archivo", comprobante);

      // Usar el mismo endpoint que en React Native
      const url = UrlApi + "/uploadfiles/uploadfiles";
            
      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });


      // Leer la respuesta
      const responseText = await response.text();

      if (!response.ok) {
        // Intentar parsear si es JSON
        try {
          const errorJson = JSON.parse(responseText);
          throw new Error(errorJson.message || `Error ${response.status}`);
        } catch (e) {
          throw new Error(`Error ${response.status}: ${responseText.substring(0, 100)}`);
        }
      }

      // Parsear la respuesta exitosa
      const result = JSON.parse(responseText);
      
      setExito(true);
      
      // Notificar al componente padre
      if (onComprobanteSubido) {
        onComprobanteSubido({
          success: true,
        //   url: result.url || result.file_url || null,
        //   nombre: comprobanteNombre,
        //   detalle: `COMPROBANTE:${comprobanteNombre}`
        });
      }
      
      // Limpiar y cerrar después de 2 segundos
      setTimeout(() => {
        handleClose();
      }, 2000);
      
    } catch (err) {
      console.error("❌ Error:", err);
      setError(err.message || "Error al subir el comprobante");
      
      if (onComprobanteSubido) {
        onComprobanteSubido({
          success: false,
          error: err.message
        });
      }
      setSubiendo(false);
    }
  };

  const handleClose = () => {
    if (!subiendo) {
      // Resetear estados
      setComprobante(null);
      setComprobanteNombre('');
      setError('');
      setExito(false);
      setSubiendo(false)
      onClose();
    }
  };

  const handleEliminarComprobante = () => {
    setComprobante(null);
    setComprobanteNombre('');
    setError('');
  };

  // Obtener icono según tipo de archivo
  const getFileIcon = () => {
    if (!comprobante) return null;
    
    const tipo = comprobante.type;
    if (tipo === 'application/pdf') {
      return <PictureAsPdfIcon sx={{ fontSize: 40, color: '#e74c3c' }} />;
    }
    return <ImageIcon sx={{ fontSize: 40, color: '#3498db' }} />;
  };

  // Formatear tamaño de archivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={subiendo}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Adjuntar Comprobante de Transferencia
          </Typography>
          {!subiendo && !exito && (
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Información del pedido */}
        {/* <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>UUID del Pedido:</strong> {uuidPedido}
          </Typography>
          <Typography variant="body2">
            <strong>Medio de pago:</strong> {medioPagoNombre}
          </Typography>
        </Alert> */}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {exito && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
            <Typography variant="body2">
              <strong>¡Comprobante subido con éxito!</strong>
            </Typography>
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              {comprobanteNombre}
            </Typography>
          </Alert>
        )}

        {subiendo && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Subiendo comprobante...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {!exito && !subiendo && (
          <>
            {!comprobante ? (
              // Botón para seleccionar archivo
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: '#27ae60',
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                <input
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  style={{ display: 'none' }}
                  id="comprobante-file-input"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="comprobante-file-input">
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    Seleccionar comprobante
                  </Button>
                </label>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                  Formatos permitidos: JPG, PNG, PDF (máx. 5MB)
                </Typography>
              </Box>
            ) : (
              // Vista previa del archivo seleccionado
              <Box sx={{ mt: 2 }}>
                <Alert 
                  severity="info" 
                  sx={{ mb: 2 }}
                  action={
                    <IconButton 
                      size="small" 
                      onClick={handleEliminarComprobante}
                      color="error"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    {getFileIcon()}
                    <Box flex={1}>
                      <Typography variant="body2" noWrap>
                        <strong>{comprobanteNombre}</strong>
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatFileSize(comprobante.size)}
                      </Typography>
                    </Box>
                  </Box>
                </Alert>

                {/* Vista previa de imagen si es JPG/PNG */}
                {comprobante.type.startsWith('image/') && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                      src={URL.createObjectURL(comprobante)}
                      alt="Vista previa"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        borderRadius: 8,
                        border: '1px solid #ddd'
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {!exito && !subiendo && (
          <>
            <Button onClick={handleClose} disabled={subiendo}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubirComprobante}
              variant="contained"
              color="primary"
              disabled={!comprobante || subiendo}
              startIcon={<CloudUploadIcon />}
            >
              Subir Comprobante
            </Button>
          </>
        )}
        {exito && !subiendo && (
          <Button onClick={handleClose} variant="contained" color="success">
            Cerrar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};